import { ZuruflexApiError } from '@zuruflex/api-client';
import { PAYOUT_METHODS, minorUnitsPerMajor, type PayoutMethod } from '@zuruflex/types';
import {
  Alert,
  Badge,
  Button,
  FormField,
  Input,
  Label,
  PageHeader,
  PasswordInput,
  Switch,
  cn,
} from '@zuruflex/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { useAuth, usePartner } from '../lib/auth.js';

export function SettingsPage() {
  const { t } = useTranslation(['partner', 'common']);
  const { partner, user } = usePartner();
  const { refreshPartner } = useAuth();

  // Payout details are the owner's business alone; the API refuses them to
  // anyone else, so the section is not rendered rather than shown failing.
  const isOwner = user.role === 'OWNER';

  return (
    <>
      <PageHeader title={t('settings.title')} description={partner.displayName} />

      <div className="flex max-w-xl flex-col gap-10">
        <BusinessSection onSaved={refreshPartner} />
        {/* Same MANAGER-and-up reach as the details above, which is what
            `PATCH /partners/me` already enforces. */}
        <DriversSection onSaved={refreshPartner} />
        {isOwner ? <PayoutSection /> : null}
        <TeamSection />
        <PasswordSection />
      </div>

      <p className="mt-10 max-w-xl text-xs text-text-muted">
        {t('settings.commission', { percent: partner.commissionBps / 100 })}.{' '}
        {t('settings.commissionHint')}
      </p>
    </>
  );
}

// ── Your details ─────────────────────────────────────────────

function BusinessSection({ onSaved }: { onSaved: () => Promise<void> }) {
  const { t } = useTranslation(['partner', 'common']);
  const { partner } = usePartner();

  const [displayName, setDisplayName] = useState(partner.displayName);
  const [legalName, setLegalName] = useState(partner.legalName ?? '');
  const [registrationNumber, setRegistrationNumber] = useState(partner.registrationNumber ?? '');
  const [contactPhone, setContactPhone] = useState(partner.contactPhone);
  const [feedback, setFeedback] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.updateMyPartner({
        displayName,
        contactPhone,
        ...(partner.kind === 'COMPANY' ? { legalName, registrationNumber } : {}),
      }),
    onSuccess: async () => {
      setFeedback({ tone: 'ok', text: t('common:state.saved') });
      await onSaved();
    },
    onError: (cause) =>
      setFeedback({
        tone: 'bad',
        text: cause instanceof ZuruflexApiError ? cause.message : t('common:error.unexpected'),
      }),
  });

  return (
    <Section title={t('settings.business')} feedback={feedback}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          setFeedback(null);
          save.mutate();
        }}
      >
        <FormField label={t('signUp.displayName')} required>
          {(field) => (
            <Input
              {...field}
              value={displayName}
              required
              onChange={(event) => setDisplayName(event.target.value)}
            />
          )}
        </FormField>

        {/* A registered business has particulars an individual does not,
            and asking an individual for a certificate number would be a
            dead end rather than a blank field. */}
        {partner.kind === 'COMPANY' ? (
          <>
            <FormField label={t('settings.legalName')}>
              {(field) => (
                <Input
                  {...field}
                  value={legalName}
                  onChange={(event) => setLegalName(event.target.value)}
                />
              )}
            </FormField>
            <FormField label={t('settings.registrationNumber')}>
              {(field) => (
                <Input
                  {...field}
                  value={registrationNumber}
                  onChange={(event) => setRegistrationNumber(event.target.value)}
                />
              )}
            </FormField>
          </>
        ) : null}

        <FormField label={t('common:field.phone')} required>
          {(field) => (
            <Input
              {...field}
              type="tel"
              value={contactPhone}
              required
              onChange={(event) => setContactPhone(event.target.value)}
            />
          )}
        </FormField>

        <Button type="submit" className="self-start" disabled={save.isPending}>
          {save.isPending ? t('common:state.working') : t('common:action.save')}
        </Button>
      </form>
    </Section>
  );
}

// ── Drivers ──────────────────────────────────────────────────

/**
 * Whether this partner supplies drivers, and what they charge for one.
 *
 * The rate is a *default*, not a price: it is copied onto a car when the car
 * is added, so a fleet is not asked the same number thirty times. Changing it
 * here re-prices nothing already listed, and the hint says so — an owner who
 * expected otherwise would raise it and quietly believe their live cars had
 * followed.
 */
function DriversSection({ onSaved }: { onSaved: () => Promise<void> }) {
  const { t } = useTranslation(['partner', 'common']);
  const { partner } = usePartner();
  const perMajor = minorUnitsPerMajor(partner.currency);

  const [provides, setProvides] = useState(partner.providesDrivers);
  const [rate, setRate] = useState(
    partner.driverDailyRate == null ? '' : String(partner.driverDailyRate / perMajor),
  );
  const [feedback, setFeedback] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.updateMyPartner({
        providesDrivers: provides,
        // Turning drivers off clears the rate rather than leaving a price
        // behind for a service no longer offered.
        driverDailyRate: provides && rate ? Math.round(Number(rate) * perMajor) : null,
      }),
    onSuccess: async () => {
      setFeedback({ tone: 'ok', text: t('common:state.saved') });
      await onSaved();
    },
    onError: (cause) =>
      setFeedback({
        tone: 'bad',
        text: cause instanceof ZuruflexApiError ? cause.message : t('common:error.unexpected'),
      }),
  });

  return (
    <Section title={t('settings.drivers')} hint={t('settings.driversHint')} feedback={feedback}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          setFeedback(null);
          save.mutate();
        }}
      >
        <div className="flex items-center gap-3">
          <Switch id="provides-drivers" checked={provides} onCheckedChange={setProvides} />
          <Label htmlFor="provides-drivers">{t('settings.providesDrivers')}</Label>
        </div>

        {provides ? (
          <FormField
            label={t('addCar.driverRate', { currency: partner.currency })}
            hint={t('settings.driverRateHint')}
          >
            {(field) => (
              <Input
                {...field}
                type="number"
                min={0}
                step={1}
                inputMode="numeric"
                value={rate}
                onChange={(event) => setRate(event.target.value)}
              />
            )}
          </FormField>
        ) : null}

        <Button type="submit" className="self-start" disabled={save.isPending}>
          {save.isPending ? t('common:state.working') : t('common:action.save')}
        </Button>
      </form>
    </Section>
  );
}

// ── Getting paid ─────────────────────────────────────────────

function PayoutSection() {
  const { t } = useTranslation(['partner', 'common']);
  const queryClient = useQueryClient();

  const { data } = useQuery({ queryKey: ['payout'], queryFn: () => api.myPayoutDetails() });

  const [method, setMethod] = useState<PayoutMethod>('MOBILE_MONEY');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [provider, setProvider] = useState('');
  const [feedback, setFeedback] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);

  // Populated once the saved details arrive, not on every render, so a
  // half-typed account number is never overwritten by a refetch.
  useEffect(() => {
    if (!data) return;
    setMethod(data.payoutMethod ?? 'MOBILE_MONEY');
    setAccountNumber(data.payoutAccountNumber ?? '');
    setAccountName(data.payoutAccountName ?? '');
    setProvider(data.payoutProvider ?? '');
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      api.setMyPayoutDetails({
        payoutMethod: method,
        payoutAccountNumber: accountNumber,
        payoutAccountName: accountName,
        payoutProvider: provider,
      }),
    onSuccess: async () => {
      setFeedback({ tone: 'ok', text: t('common:state.saved') });
      await queryClient.invalidateQueries({ queryKey: ['payout'] });
    },
    onError: (cause) =>
      setFeedback({
        tone: 'bad',
        text: cause instanceof ZuruflexApiError ? cause.message : t('common:error.unexpected'),
      }),
  });

  return (
    <Section title={t('settings.payout')} hint={t('settings.payoutHint')} feedback={feedback}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          setFeedback(null);
          save.mutate();
        }}
      >
        <fieldset className="flex flex-col gap-1.5">
          <legend className="mb-1.5 text-sm font-medium text-text">{t('settings.method')}</legend>
          <div className="grid grid-cols-2 gap-2">
            {PAYOUT_METHODS.map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={method === option}
                onClick={() => setMethod(option)}
                className={cn(
                  'rounded-field border px-3 py-2 text-sm font-medium transition-colors',
                  method === option
                    ? 'border-surface-inverse bg-surface-inverse text-text-inverse'
                    : 'border-border-default hover:border-border-strong',
                )}
              >
                {option === 'MOBILE_MONEY' ? 'Mobile money' : 'Bank transfer'}
              </button>
            ))}
          </div>
        </fieldset>

        <FormField label={t('settings.provider')} required>
          {(field) => (
            <Input
              {...field}
              value={provider}
              placeholder={method === 'MOBILE_MONEY' ? 'MTN' : 'Absa'}
              required
              onChange={(event) => setProvider(event.target.value)}
            />
          )}
        </FormField>

        <FormField label={t('settings.accountNumber')} required>
          {(field) => (
            <Input
              {...field}
              value={accountNumber}
              required
              onChange={(event) => setAccountNumber(event.target.value)}
            />
          )}
        </FormField>

        <FormField label={t('settings.accountName')} required>
          {(field) => (
            <Input
              {...field}
              value={accountName}
              required
              onChange={(event) => setAccountName(event.target.value)}
            />
          )}
        </FormField>

        <Button type="submit" className="self-start" disabled={save.isPending}>
          {save.isPending ? t('common:state.working') : t('common:action.save')}
        </Button>
      </form>
    </Section>
  );
}

// ── Who can sign in ──────────────────────────────────────────

function TeamSection() {
  const { t } = useTranslation(['partner', 'common']);
  const { data } = useQuery({ queryKey: ['team'], queryFn: () => api.myTeam() });

  return (
    <Section title={t('settings.team')}>
      <ul className="flex flex-col gap-2">
        {data?.map((member) => (
          <li
            key={member.id}
            className="flex flex-wrap items-center justify-between gap-3 rounded-field border border-border-default px-3 py-2"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{member.fullName}</p>
              <p className="truncate text-xs text-text-muted">{member.email}</p>
            </div>
            <div className="flex items-center gap-2">
              {member.hasGoogle ? <Badge variant="outline">Google</Badge> : null}
              {member.hasApple ? <Badge variant="outline">Apple</Badge> : null}
              <Badge>{t(`common:partnerRole.${member.role}`)}</Badge>
            </div>
          </li>
        ))}
      </ul>
    </Section>
  );
}

// ── Password ─────────────────────────────────────────────────

function PasswordSection() {
  const { t } = useTranslation(['partner', 'common']);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [feedback, setFeedback] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);

  const save = useMutation({
    mutationFn: () => api.setPartnerPassword(newPassword, currentPassword || undefined),
    onSuccess: () => {
      setCurrentPassword('');
      setNewPassword('');
      setFeedback({ tone: 'ok', text: t('common:state.saved') });
    },
    onError: (cause) =>
      setFeedback({
        tone: 'bad',
        text: cause instanceof ZuruflexApiError ? cause.message : t('common:error.unexpected'),
      }),
  });

  return (
    <Section title={t('settings.password')} feedback={feedback}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          setFeedback(null);
          save.mutate();
        }}
      >
        {/* Optional, because an account that has only ever used Google has
            no current password to prove — the API decides, not the form. */}
        <FormField label={t('settings.currentPassword')}>
          {(field) => (
            <PasswordInput
              {...field}
              value={currentPassword}
              autoComplete="current-password"
              onChange={(event) => setCurrentPassword(event.target.value)}
              showLabel={t('common:action.showPassword')}
              hideLabel={t('common:action.hidePassword')}
            />
          )}
        </FormField>

        <FormField label={t('settings.newPassword')} required>
          {(field) => (
            <PasswordInput
              {...field}
              value={newPassword}
              autoComplete="new-password"
              minLength={12}
              required
              onChange={(event) => setNewPassword(event.target.value)}
              showLabel={t('common:action.showPassword')}
              hideLabel={t('common:action.hidePassword')}
            />
          )}
        </FormField>

        <Button type="submit" className="self-start" disabled={save.isPending}>
          {save.isPending ? t('common:state.working') : t('settings.changePassword')}
        </Button>
      </form>
    </Section>
  );
}

function Section({
  title,
  hint,
  feedback,
  children,
}: {
  title: string;
  hint?: string;
  feedback?: { tone: 'ok' | 'bad'; text: string } | null;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h2 className="font-display text-lg font-bold">{title}</h2>
      {hint ? <p className="mt-1 text-sm text-text-muted">{hint}</p> : null}
      {feedback ? (
        <Alert variant={feedback.tone === 'ok' ? 'success' : 'danger'} className="mt-3">
          {feedback.text}
        </Alert>
      ) : null}
      <div className="mt-4">{children}</div>
    </section>
  );
}
